import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { eyebrowClass, cardClass } from '@/lib/sectionStyles';

const SECURITY_CARDS = [
  {
    title: 'Read-only, always',
    description:
      'Discovery connectors never require write scope, and every connector ships the exact permission manifest — hand it to your security team verbatim.',
  },
  {
    title: 'Metadata only',
    description:
      'Never prompts, source code, secret values, or packet payloads. We record that an agent exists, what it can reach, and who owns it.',
  },
  {
    title: 'Credentials never displayed',
    description:
      'Write-only from the portal into the platform vault; refresh tokens never leave the coordination plane; agents receive just-in-time, scope-bound, short-TTL credentials.',
  },
  {
    title: 'It fails loudly',
    description:
      'A broken connector shows unhealthy and raises a signal. It never quietly reports "no new agents" — silent breakage is the failure mode we designed against.',
  },
  {
    title: 'Per-customer isolation',
    description:
      'Every customer runs a dedicated single-tenant coordinator with its own database, cache, and storage. Provisioning tiers add a dedicated cloud project, customer-managed keys with a kill switch, and a private-IP data plane.',
  },
  {
    title: 'SOC 2 Type II',
    description:
      'Independently audited controls across the platform, with SSO, RBAC, field-level security, and immutable audit logs as standard.',
  },
];

const COMPLIANCE_ITEMS = [
  {
    lead: 'NAIC Model AI Bulletin',
    rest: 'enforced evidence fields, bias schedules, consumer notice, accountability structure, shadow reports',
  },
  {
    lead: 'EU AI Act',
    rest: 'high-risk-ready control mapping on the same record fabric',
  },
  {
    lead: 'NIST AI RMF 1.0',
    rest: 'alignment across the control set',
  },
  {
    lead: 'Audit integrity',
    rest: 'immutable, hash-chained records with server-recomputed verification and separation-of-duties break-glass access',
  },
];

export function TrustPage() {
  return (
    <>
      <Helmet>
        <title>Trust & Security - CriticalBridge.AI</title>
        <meta
          name="description"
          content="One page for your security reviewer, your compliance officer, and your architect — what we can see, what we can never see, and how the evidence holds up."
        />
        <meta
          name="keywords"
          content="ai agent security, agentic ai compliance, NAIC AI bulletin, EU AI Act, SOC 2 agentic AI"
        />
        <meta property="og:title" content="Trust & Security - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="Read-only, by construction. Isolated, by architecture."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/trust" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Trust
          </div>
          <h1 className="mt-4 max-w-[24ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Read-only, by construction. Isolated, by architecture.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            One page for your security reviewer, your compliance officer, and your architect — what
            we can see, what we can never see, and how the evidence holds up.
          </p>
        </div>
      </section>

      {/* Security architecture */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Security architecture</span>
          <h2 className="mt-2 text-3xl font-bold">
            What your security team will ask — answered up front.
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {SECURITY_CARDS.map((card) => (
              <div key={card.title} className={cardClass}>
                <h3 className="text-lg font-bold">{card.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance depth */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Compliance depth</span>
          <h2 className="mt-2 text-3xl font-bold">
            Frameworks as enforced fields, not marketing labels.
          </h2>

          <ul className="mt-4 grid max-w-[74ch] gap-2.5">
            {COMPLIANCE_ITEMS.map((item) => (
              <li key={item.lead} className="relative pl-6 text-base text-muted-foreground">
                <span className="absolute left-0 top-2 h-2.5 w-2.5 rounded-sm bg-primary" />
                <strong className="text-foreground">{item.lead}</strong> — {item.rest}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Open standards */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Open standards</span>
          <h2 className="mt-2 text-3xl font-bold">Trust you can verify from outside.</h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            A2A is the backbone; MCP servers are first-class governed citizens; DID-based identity
            and signed trust manifests back the public AI Catalog — where a quarantined agent's
            public trust badge drops in real time. Published wire contracts are schema-pinned in CI,
            so federation peers never discover a broken contract in production.
          </p>
          <Button variant="outline" className="mt-6" asChild>
            <Link to="/trial">Start with read-only discovery →</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
